import React from 'react';
import * as Yup from 'yup';
import { isAxiosError } from 'axios';
import { Box, Collapse, LinearProgress, Paper, Typography } from '@mui/material';
import { InsertDriveFileOutlined } from '@mui/icons-material';
import { Formik } from 'formik';
import { Modal, ModalActions, ModalContent, ModalTitle } from '../../../../../Modal';
import { Button } from '../../../../../Button';
import { Select, TextInput } from '../../../../../Inputs';
import { SubmitButton } from '../../../../../Form';
import { Alert, AlertInline } from '../../../../../Alert';
import { PatientUploadedMedicalDocument, UploadPurpose, createPatientMedicalDocumentRecord, medicalDocumentUploadReasons, updatePatientMedicalDocumentRecord, uploadFileToS3 } from '@uplift/api';
import { useAlert } from 'react-alert';
import { useTimeoutResettableState } from '@uplift/hooks';


export type DocumentDetailsFormProps = {
  patientId: string;
  file: File;
  onCancel: () => void;
  onSuccess: (doc: PatientUploadedMedicalDocument) => Promise<void>;
};

const documentUploadReasonsOptions = medicalDocumentUploadReasons.map(item => ({ value: item, label: item }));


export const documentDetailsValidationSchema = Yup.object().shape({
  documentType: Yup.string().nullable().required('A document label is required'),
  description: Yup.string().max(256).transform(v => v?.replace(/\W/ig, '')).required('A document description is required'),
});


export const DocumentDetailsFields = () => {
  const [ warningShown, setWarningShown ] = useTimeoutResettableState(5000, false);

  return (
    <Box display="flex" flexDirection="column" gap="24px">
      <Box position="absolute" left="0" top="0" width="100%" display="flex" alignItems="center" justifyContent="center" zIndex="2">
        <Collapse in={warningShown}>
          <Alert severity="info" icon={null}>Maximum character count reached</Alert>
        </Collapse>
      </Box>
      <Box width="345px">
        <Select
          color="secondary"
          name="documentType"
          label="What is this document?"
          items={documentUploadReasonsOptions}
        />
      </Box>
      <TextInput
        maxCharacters={256}
        onCharactersLimit={() => {
          setWarningShown(true);
        }}
        name="description"
        color="secondary"
        label="Document description"
        fullWidth
        multiline
        minRows={2}
        maxRows={3}
        helperText="Use this space to add details that could help another provider understand this document"
        mixHelperTextAndError
      />
    </Box>
  );
};


export const DocumentDetailsForm: React.VFC<DocumentDetailsFormProps> = (props) => {
  return (
    <Formik
      onSubmit={async (values, helpers) => {
        if (!values.documentType) return;
        if (!values.description) return;

        try {
          const uploadedReq = await uploadFileToS3(props.patientId, UploadPurpose.patientDocument, props.file);
          const response = await createPatientMedicalDocumentRecord(props.patientId, {
            fileUploadId: uploadedReq.file_upload.id,
            reasonForUpload: values.documentType,
            additionalDetails: values.description
          });

          await props.onSuccess(response.data);
        } catch (err) {
          if (isAxiosError(err)) {
            helpers.setStatus({ type: 'error', message: err.response?.data?.error || { 'ERR_NETWORK': "We couldn't upload this file because of a network error. Check your internet connection and try again or upload later." }[err.code] || err.message });
          }
        }
      }}
      initialErrors={{ description: ' ' }}
      initialValues={{
        documentType: null,
        description: '',
      } as {
        documentType: string | null;
        description: string;
      }}
      validationSchema={documentDetailsValidationSchema}
    >
      {formik => {
        const hasSubmissionError = formik.status?.type === 'error';
        const submittingOrError = formik.isSubmitting || hasSubmissionError;

        return (
          <>
            <ModalTitle>{formik.isSubmitting ? 'Uploading...' : 'Add description'}</ModalTitle>
            <ModalContent noPadding>
              <Box bgcolor="grey.50" p={3}>
                <Paper variant="outlined">
                  <Box p={2} display="flex" gap="8px" alignItems="center" fontWeight={600}>
                    <Box p="3px" bgcolor="grey.300" fontSize="18px" display="flex" borderRadius="50%">
                      <InsertDriveFileOutlined htmlColor="rgba(0,0,0,0.38)" fontSize="inherit" />
                    </Box>
                    <Typography component="span" style={{ fontWeight: 600 }}>
                      {submittingOrError ? formik.values.documentType : props.file.name}
                    </Typography>
                  </Box>
                  <Collapse in={formik.isSubmitting}>
                    <Box px={2} pb={2}>
                      <LinearProgress color="secondary" />
                    </Box>
                  </Collapse>
                  <Collapse in={hasSubmissionError}>
                    <Box px={2} pb={2}>
                      <AlertInline severity="error">
                        {formik.status?.message}
                      </AlertInline>
                    </Box>
                  </Collapse>
                </Paper>
              </Box>
              <Collapse in={!submittingOrError}>
                <Box p={3}>
                  <DocumentDetailsFields />
                </Box>
              </Collapse>
            </ModalContent>
            <ModalActions noPadding>
              <Collapse in={!formik.isSubmitting}>
                <Box p={2} display="flex" gap="8px">
                  <Button disabled={formik.isSubmitting} onClick={props.onCancel}>Cancel</Button>
                  <SubmitButton color="secondary">{hasSubmissionError ? 'Try again' : 'Upload'}</SubmitButton>
                </Box>
              </Collapse>
            </ModalActions>
          </>
        );
      }}
    </Formik>
  );
};

export const EditDocumentDetailsModal: React.VFC<{
  open: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
  document: PatientUploadedMedicalDocument;
}> = (props) => {
  const alert = useAlert();

  return (
    <Formik
      onSubmit={async values => {
        if (!values.documentType) return;
        if (!values.description) return;

        try {
          await updatePatientMedicalDocumentRecord(props.document.patientId, props.document.id, {
            reasonForUpload: values.documentType,
            additionalDetails: values.description
          });
          await props.onSuccess();
          alert.success('Document updated');
        } catch (err) {
          alert.error(<>There was a problem updating the document. Try again or contact <strong>help@joinuplift.co</strong></>);
          throw err;
        }

      }}
      initialErrors={{ description: ' ' }}
      initialValues={{
        documentType: props.document.reasonForUpload,
        description: props.document.additionalDetails,
      } as {
        documentType: string;
        description: string;
      }}
      validationSchema={documentDetailsValidationSchema}
    >
      {formik => (
        <Modal
          open={props.open}
          onClose={props.onClose}
          title="Edit document"
          maxWidth="md"
          PaperProps={{ style: { maxWidth: '600px' } }}
          fullWidth
        >
          <ModalContent>
            <DocumentDetailsFields />
          </ModalContent>
          <ModalActions>
            <Button disabled={formik.isSubmitting} onClick={props.onClose}>Cancel</Button>
            <SubmitButton color="secondary">Done</SubmitButton>
          </ModalActions>
        </Modal>
      )}
    </Formik>
  );
};
